import { useState, type ReactNode } from 'react'
import type { BenchSummary, ConferenceTimeReport, IntervalSummary } from '../lib/api.ts'
import { AccountFilter } from '../components/AccountFilter.tsx'
import { Button } from '../components/Button.tsx'
import { DateRange, type DateWindow } from '../components/DateRange.tsx'
import { Input } from '../components/Input.tsx'
import {
  benchNote,
  defaultWindows,
  formatChange,
  formatDuration,
  formatHours,
  formatShare,
} from '../lib/conferenceTime.ts'
import { useConferenceTime } from '../lib/useConferenceTime.ts'

/**
 * How long conferência takes, before and after (#406).
 *
 * A number for pricing the product, so it is framed the way the pharmacy
 * would want it said: bench time given back to the pharmacist, never errors
 * found -- and never per person. Two periods side by side, because the
 * question is always "compared with when".
 *
 * Definitions and caveats live in `docs/technical/conference-time-metric.md`;
 * the short versions are on the screen, next to the numbers they qualify.
 *
 * A tab of Relatórios (#551): the heading and the way back are that screen's.
 */

type Cohort = 'all' | 'withReading' | 'withoutReading'

const COHORTS: readonly { readonly id: Cohort; readonly label: string }[] = [
  { id: 'all', label: 'Todas' },
  { id: 'withReading', label: 'Com a leitura da IA' },
  { id: 'withoutReading', label: 'Sem a leitura da IA' },
]

interface Hours {
  weekday: string
  saturday: string
  sunday: string
  holidays: string
}

const DEFAULT_HOURS: Hours = {
  weekday: '08:00-18:00',
  saturday: '08:00-12:00',
  sunday: '',
  holidays: '',
}

interface Row<S> {
  readonly label: string
  readonly hint?: string
  readonly value: (s: S) => string
  /** The raw figure a relative change is computed on, when one makes sense. */
  readonly compare?: (s: S) => number | undefined
}

function durationRows<S extends IntervalSummary>(countOf: (s: S) => number): Row<S>[] {
  return [
    {
      label: 'Relógio · mediana',
      hint: 'Da entrada à saída, com noites e fins de semana.',
      value: (s) => formatDuration(s.wallClock.median),
      compare: (s) => s.wallClock.median,
    },
    {
      label: 'Relógio · média',
      value: (s) => formatDuration(s.wallClock.mean),
      compare: (s) => s.wallClock.mean,
    },
    {
      label: 'Relógio · p75 / p90',
      value: (s) => `${formatDuration(s.wallClock.p75)} / ${formatDuration(s.wallClock.p90)}`,
    },
    {
      label: 'Horário comercial · mediana',
      hint: 'Só o que caiu dentro do horário informado. Hora extra não conta.',
      value: (s) => formatDuration(s.business.median),
      compare: (s) => s.business.median,
    },
    {
      label: 'Horário comercial · média',
      value: (s) => formatDuration(s.business.mean),
      compare: (s) => s.business.mean,
    },
    {
      label: 'Horário comercial · p75 / p90',
      value: (s) => `${formatDuration(s.business.p75)} / ${formatDuration(s.business.p90)}`,
    },
    { label: 'Horário comercial · total', value: (s) => formatHours(s.business.total) },
    {
      label: 'Mesmo dia · quantas / mediana',
      hint: 'Entraram e saíram no mesmo dia: a amostra que não depende do horário.',
      value: (s) => `${s.sameDay.count} / ${formatDuration(s.sameDay.median)}`,
      compare: (s) => s.sameDay.median,
    },
    {
      label: 'Terminaram fora do horário',
      hint: 'Sinal de hora extra: quanto maior, mais o horário comercial subestima.',
      value: (s) => `${s.endedOutsideHours} (${formatShare(s.endedOutsideHours, countOf(s))})`,
    },
  ]
}

const BENCH_ROWS: readonly Row<BenchSummary>[] = [
  { label: 'Entraram na conferência', value: (s) => String(s.entered), compare: (s) => s.entered },
  { label: 'Saíram (com tempo medido)', value: (s) => String(s.measured) },
  { label: 'Ainda na conferência', value: (s) => String(s.open) },
  {
    label: 'Foram à produção sem passar pela conferência',
    hint: 'Sem leitura na conferência do ERP. Contadas, não cronometradas.',
    value: (s) => String(s.bypassed),
  },
  { label: 'Voltaram à conferência depois de sair', value: (s) => String(s.returned) },
  ...durationRows<BenchSummary>((s) => s.measured),
]

const VERIFIER_ROWS: readonly Row<ConferenceTimeReport['verifier']>[] = [
  {
    label: 'Ficaram prontas para conferir',
    value: (s) => String(s.ready),
    compare: (s) => s.ready,
  },
  { label: 'Conferidas', value: (s) => String(s.verified) },
  ...durationRows<ConferenceTimeReport['verifier']>((s) => s.verified),
]

export function ConferenceTime({ sessionAccountId }: { sessionAccountId?: string | undefined }) {
  const initial = defaultWindows(new Date())
  // Whose bench (#640): the session's until a super admin picks another, as on Regras.
  const [picked, setPicked] = useState<string | undefined>()
  const account = picked ?? sessionAccountId
  const [before, setBefore] = useState<DateWindow>(initial.before)
  const [after, setAfter] = useState<DateWindow>(initial.after)
  const [draft, setDraft] = useState<Hours>(DEFAULT_HOURS)
  const [hours, setHours] = useState<Hours>(DEFAULT_HOURS)
  const [cohort, setCohort] = useState<Cohort>('all')
  const [nonce, setNonce] = useState(0)

  const query = (w: DateWindow) =>
    w.from === '' || w.to === '' ? undefined : { from: w.from, to: w.to, ...hours, account }
  const a = useConferenceTime(query(before), nonce)
  const b = useConferenceTime(query(after), nonce)

  return (
    <div className="flex flex-col gap-5">
      <p className="text-tP text-text-weak max-w-prose">
        Quanto tempo a requisição fica na bancada de conferência, lido das leituras de código de
        barras do ERP — antes e depois do sistema, com o mesmo instrumento. É demanda da bancada,
        não tempo de trabalho de ninguém: inclui fila, almoço e fim de expediente. Não há números
        por pessoa, de propósito.
      </p>

      <Button
        variant="secondary"
        tone="neutral"
        size="small"
        className="self-start"
        onClick={() => setNonce((n) => n + 1)}
      >
        Ler o ERP de novo
      </Button>

      <section className="flex flex-wrap items-end gap-3">
        <AccountFilter value={account ?? ''} onChange={setPicked} allowAll={false} />
        <DateRange label="Antes" value={before} onChange={setBefore} />
        <DateRange label="Depois" value={after} onChange={setAfter} />
        <div className="flex gap-1" role="group" aria-label="Quais requisições">
          {COHORTS.map((c) => (
            <Button
              key={c.id}
              size="small"
              variant={cohort === c.id ? 'primary' : 'secondary'}
              tone={cohort === c.id ? 'brand' : 'neutral'}
              onClick={() => setCohort(c.id)}
            >
              {c.label}
            </Button>
          ))}
        </div>
      </section>

      <section className="flex flex-wrap items-end gap-3">
        <Input
          size="small"
          label="Seg a sex"
          value={draft.weekday}
          onChange={(e) => setDraft({ ...draft, weekday: e.target.value })}
          containerClassName="w-44"
        />
        <Input
          size="small"
          label="Sábado"
          value={draft.saturday}
          onChange={(e) => setDraft({ ...draft, saturday: e.target.value })}
          containerClassName="w-36"
        />
        <Input
          size="small"
          label="Domingo"
          placeholder="fechado"
          value={draft.sunday}
          onChange={(e) => setDraft({ ...draft, sunday: e.target.value })}
          containerClassName="w-36"
        />
        <Input
          size="small"
          label="Feriados"
          placeholder="2026-09-07, 2026-10-12"
          value={draft.holidays}
          onChange={(e) => setDraft({ ...draft, holidays: e.target.value })}
          containerClassName="w-64"
        />
        <Button variant="secondary" tone="neutral" size="small" onClick={() => setHours(draft)}>
          Aplicar horário
        </Button>
      </section>
      <p className="text-tSmall text-text-weak max-w-prose">
        Horário no formato 08:00-12:00,13:00-18:00; vazio é fechado. Muda só a coluna de horário
        comercial — o relógio e a amostra do mesmo dia não dependem dele.
      </p>

      <Section
        title="Na bancada (ERP)"
        note={benchNote(a.report?.stagePositions ?? b.report?.stagePositions)}
      >
        <Status label="Antes" report={a.report} error={a.error} />
        <Status label="Depois" report={b.report} error={b.error} />
        <Comparison
          rows={BENCH_ROWS}
          before={a.report?.bench[cohort]}
          after={b.report?.bench[cohort]}
        />
      </Section>

      <Section
        title="Na conferência do app"
        note="Do momento em que a leitura ficou pronta para o farmacêutico até ele concluir. Só existe onde o sistema rodou; uma ida à correção fica dentro do tempo."
      >
        <Comparison rows={VERIFIER_ROWS} before={a.report?.verifier} after={b.report?.verifier} />
      </Section>
    </div>
  )
}

function Section({ title, note, children }: { title: string; note: string; children: ReactNode }) {
  return (
    <section className="flex flex-col gap-2">
      <h2 className="text-tPSemi text-text-strong">{title}</h2>
      <p className="text-tSmall text-text-weak max-w-prose">{note}</p>
      {children}
    </section>
  )
}

/** Where one period's ERP read has got to, in words. */
function Status({
  label,
  report,
  error,
}: {
  label: string
  report: ConferenceTimeReport | undefined
  error: string | undefined
}) {
  if (error !== undefined) {
    return <p className="text-tSmall text-text-error">{`${label}: ${error}`}</p>
  }
  if (report === undefined)
    return <p className="text-tSmall text-text-weak">{`${label}: carregando…`}</p>
  const bench = report.bench
  if (bench.state === 'running') {
    return (
      <p className="text-tSmall text-text-weak">
        {`${label}: lendo o ERP, semana ${bench.chunksDone ?? 0} de ${bench.chunksTotal ?? '?'}…`}
      </p>
    )
  }
  if (bench.state === 'failed') {
    return (
      <p className="text-tSmall text-text-error">
        {`${label}: a leitura do ERP falhou (${bench.error ?? 'sem detalhe'}). A farmácia costuma desligar o servidor à noite — tente “Ler o ERP de novo” pela manhã.`}
      </p>
    )
  }
  if (bench.state === 'unavailable') {
    return (
      <p className="text-tSmall text-text-weak">{`${label}: ${bench.error ?? 'ERP indisponível'}`}</p>
    )
  }
  return null
}

function Comparison<S>({
  rows,
  before,
  after,
}: {
  rows: readonly Row<S>[]
  before: S | undefined
  after: S | undefined
}) {
  return (
    <div className="rounded-base border-stroke-weak bg-background-raised w-full overflow-x-auto border">
      <table className="w-full border-collapse">
        <thead>
          <tr>
            {['', 'Antes', 'Depois', 'Variação'].map((h) => (
              <th
                key={h}
                scope="col"
                className="border-stroke-weak bg-background-sunken text-tPSemi text-text-strong border-b px-3 py-3 text-left"
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.label}>
              <td className="border-stroke-weak text-tP text-text-strong border-b px-3 py-3">
                {row.label}
                {row.hint !== undefined && (
                  <span className="text-tSmall text-text-weak block">{row.hint}</span>
                )}
              </td>
              <td className="border-stroke-weak text-tP text-text-strong border-b px-3 py-3 tabular-nums">
                {before === undefined ? '—' : row.value(before)}
              </td>
              <td className="border-stroke-weak text-tP text-text-strong border-b px-3 py-3 tabular-nums">
                {after === undefined ? '—' : row.value(after)}
              </td>
              <td className="border-stroke-weak text-tP text-text-weak border-b px-3 py-3 tabular-nums">
                {row.compare === undefined || before === undefined || after === undefined
                  ? ''
                  : formatChange(row.compare(before), row.compare(after))}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
